import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import {
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM,
  UI_CAPTION_BOLD,
} from "oolib/dist/v2/components/Typo2";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { PillDropdown } from "../../comps/PillDropdown";
import { StatusPill } from "../../comps/StatusPill";
import { useDetailPanel } from "../../comps/ActivePanelContext";
import { useGetTeamLens, useGetTagDetail } from "../../hooks/useGetLensData";
import { ConfidenceIndicator } from "../comps/ConfidenceIndicator";
import { TransparencyPanel } from "../comps/TransparencyPanel";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { WIDGET_STYLE, WIDGET_BODY_STYLE, getStatusTheme } from "../constants";
import { SealWarning } from "@phosphor-icons/react";

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const IssueList = styled.div`
  display: flex;
  flex-direction: column;
`;

const IssueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const IssueNumber = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors2.grey10};
  color: ${colors2.grey70};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IssueLabel = styled.div`
  flex: 1;
  min-width: 0;
  color: ${colors2.grey80};
`;

const IssueRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export function TeamLensBlock({ period, locality, showTransparency }) {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [view, setView] = useState({ value: "numbers", display: "Mentions" });
  const { selected: selectedTag, select: selectTag, deselect: deselectTag, isOpen: tagPanelOpen } = useDetailPanel("lenses:team");

  // Close sidebar when period changes so it doesn't show stale data
  useEffect(() => { deselectTag(); }, [period]);

  // Always fetch all teams — filter client-side when a specific category is selected
  const { data, isLoading } = useGetTeamLens({
    team: null,
    period,
    view: view?.value,
    locality,
  });

  const { data: tagDetail, isLoading: tagDetailLoading } = useGetTagDetail({
    tagId: selectedTag?.tagId,
    period,
    mode: "issues",
    locality,
    enabled: !!selectedTag,
  });

  const teamOptions = (data?.availableTeams || []).map((t) => ({
    value: t.key,
    display: t.label,
  }));

  // Filter to selected category or show all, then flatten + sort by count
  const teamsToShow = selectedTeam?.value && data?.teams?.[selectedTeam.value]
    ? { [selectedTeam.value]: data.teams[selectedTeam.value] }
    : data?.teams || {};
  const allIssues = [];
  for (const [, team] of Object.entries(teamsToShow)) {
    for (const issue of (team.issues || [])) {
      allIssues.push(issue);
    }
  }
  allIssues.sort((a, b) => b.currentCount - a.currentCount);

  return (
    <>
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <SealWarning size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF style={{ display: "flex", gap: "0.5rem" }}>
            Issues in {teamOptions.length > 0 && (
                <PillDropdown
                  value={selectedTeam || { value: "", display: "All Topic Categories" }}
                  onChange={(id, val) => setSelectedTeam(val?.value ? val : null)}
                  options={[{ value: "", display: "All Topic Categories" }, ...teamOptions]}
                />
              )}
          </UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={WIDGET_STYLE}
      bodyStyle={WIDGET_BODY_STYLE}
    >
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0" }}>
              <SkeletonLoader style={{ height: 14, width: `${40 + Math.random() * 30}%`, borderRadius: 4 }} />
              <SkeletonLoader style={{ height: 22, width: 60, borderRadius: 12 }} />
            </div>
          ))}
        </div>
      )}
      {!isLoading && allIssues.length === 0 && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No team data available yet.</UI_BODY_SM>
      )}
      {!isLoading && (
        <IssueList>
          {allIssues.slice(0, 10).map((issue, idx) => {
            const trendTheme = getStatusTheme("trend", issue.trend);
            return (
              <IssueRow
                key={issue.tagId}
                onClick={() => selectTag(issue)}
              >
                <IssueNumber>
                  <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey70 }}>{idx + 1}</UI_BODY_SEMIBOLD_SM_DF>
                </IssueNumber>
                <IssueLabel>
                  <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>
                    {issue.name}
                  </UI_BODY_SEMIBOLD_SM_DF>
                </IssueLabel>
                <IssueRight>
                  <div style={{ minWidth: 60, display: 'flex', justifyContent: 'center' }}>
                    <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey60 }}>{issue.currentCount}</UI_BODY_SEMIBOLD_SM_DF>
                  </div>
                  <div style={{ minWidth: 100, display: 'flex', justifyContent: 'flex-end' }}>
                    <StatusPill theme={trendTheme} />
                  </div>
                </IssueRight>
              </IssueRow>
            );
          })}
          {showTransparency && data?.teams && Object.values(data.teams)[0]?.coverage && (
            <>
              <ConfidenceIndicator
                confidence={Object.values(data.teams)[0].coverage.confidence}
                summary={Object.values(data.teams)[0].coverage.summary}
              />
              <TransparencyPanel warnings={Object.values(data.teams)[0].coverage.warnings} />
            </>
          )}
        </IssueList>
      )}
    </DashboardWidgetShell>

    {(() => {
      const tagTheme = getStatusTheme("trend", selectedTag?.trend || "stable");
      return (
        <TagDetailSidebar
          isOpen={tagPanelOpen}
          onClose={deselectTag}
          isLoading={tagDetailLoading}
          title={selectedTag?.name}
          mentionsCount={selectedTag?.currentCount}
          description={tagDetail?.description}
          sources={tagDetail?.sources}
          trendData={tagDetail?.trend}
          mentions={(tagDetail?.annotations || []).map((a) => ({ tone: a.sentiment, text: a.text, fragment: a.fragment }))}
          pill={<StatusPill theme={tagTheme} as="span" />}
          primaryColor={tagTheme.accent}
          glowColor={tagTheme.glow}
        />
      );
    })()}
    </>
  );
}
